package Structs

import DataBase.UINKDBInterface
import Utils.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName

@myName("ClientStaticData")
@Serializable
data class ClientStaticData(
    val id: Int,
    var business_name: String,
    val business_id: String="",
    var address: String = "",
    val phone: String = "",
    /** When true, PCN874 files this purchase as equipment inputs (R) instead of other inputs (T). */
    var supplier_input_equipment: Boolean = false
) {
    var city: String = ""
    var street: String = ""
    var streetNumber: String = ""
    var  isClient: Boolean = true
    init {
        business_name = fixJsonInternal(business_name ?: "")
        address = fixJsonInternal(address ?: "")
        val addressAr = if (address.isNotEmpty()) address!!.split(",") else listOf("", "", "")
        city = addressAr[0]
        street = addressAr?.getOrNull(1)?:""
        streetNumber = addressAr.getOrNull(2) ?: ""
    }

    @myName("getAddressStr")
    open fun getAddressStr(): String {

        return "${street} ${streetNumber} ${city}".trim()
    }

    /** True when at least one forced field (כתובת/שם עסק מאולץ etc.) is set. */
    fun hasForcedFields(): Boolean =
        business_name.isNotEmpty() || address.isNotEmpty() ||
            business_id.isNotEmpty() || phone.isNotEmpty()

    /**
     * Prefer non-empty forced fields from [orderStatic] (order.static_data) over this
     * snapshot (usually the live client). Used when creating a tax note / invoice from an order.
     */
    fun withOrderForcedOverrides(orderStatic: ClientStaticData?): ClientStaticData {
        if (orderStatic == null || !orderStatic.hasForcedFields()) return this
        return ClientStaticData(
            id = if (id != 0) id else orderStatic.id,
            business_name = orderStatic.business_name.takeIf { it.isNotEmpty() } ?: business_name,
            business_id = orderStatic.business_id.takeIf { it.isNotEmpty() } ?: business_id,
            address = orderStatic.address.takeIf { it.isNotEmpty() } ?: address,
            phone = orderStatic.phone.takeIf { it.isNotEmpty() } ?: phone,
        )
    }

    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["id"] = JsonPrimitive(id.toString())
        map["business_name"] = JsonPrimitive(prepareStrToJson(business_name))
        map["business_id"] = JsonPrimitive(business_id)
        map["address"] = JsonPrimitive(prepareStrToJson(address))
        map["phone"] = JsonPrimitive(prepareStrToJson(phone))
        if(supplier_input_equipment)
        map["supplier_input_equipment"] = JsonPrimitive(supplier_input_equipment)
        return JsonObject(map)
    }
    @myName("getClient")
    fun getClient(): Entity {
        return if(isClient){
            UINKDBInterface.activeDB.getClient(id).first!!
        } else {
            UINKDBInterface.activeDB.getSupplier(id).first!!
        }

    }
    companion object {
        fun createFromJson(js: String): ClientStaticData {
            val trimmed = js.trim()
            if (trimmed.isEmpty() || trimmed == "[]" || trimmed == "null" || trimmed == "None") {
                return ClientStaticData(0, "")
            }
            return when (val element = getJsonWithConfig().parseToJsonElement(trimmed.replace("\\'", "'"))) {
                is JsonNull -> ClientStaticData(0, "")
                is JsonArray -> {
                    if (element.isEmpty()) ClientStaticData(0, "")
                    else element.map {
                        getJsonWithConfig().decodeFromJsonElement(ClientStaticData.serializer(), it)
                    }.first()
                }
                is JsonObject ->
                    getJsonWithConfig().decodeFromJsonElement(ClientStaticData.serializer(), element)
                else -> ClientStaticData(0, "")
            }
        }

        fun toJsonArrayString(data: ClientStaticData): String {
            return "[${getJsonWithConfig().encodeToString(JsonObject.serializer(), data.toJson())}]"

        }
    }

}

/** Which business identity a document should be issued under. */
@myName("BusinessIdentityMode")
enum class BusinessIdentityMode {
    /** Follow the network's configured [NetworkBillingMode]. */
    AUTO,

    /** Force the network master's identity. */
    NETWORK,

    /** Force the branch's own identity, falling back to the network when it has none. */
    BRANCH
}

/**
 * Builds the identity block that gets frozen onto a document in
 * `client_tax_note.details`. Every invoice, receipt and credit note goes through
 * here so the same client always resolves the same way, whichever screen issued it.
 *
 * All four fields come from whichever entity is being billed, so the address and
 * phone always belong to the same legal entity as the שם עוסק and ח.פ. A branch can
 * never resolve to a blank ח.פ: without a valid own identity it falls back to the
 * network's. A previous month whose dated row still has an empty מס עוסק uses the
 * current client or master id instead of leaving the document blank.
 */
@myName("buildStaticData")
fun Client.buildStaticData(
    date: String = DatesManipulator.dateNow(),
    mode: BusinessIdentityMode = BusinessIdentityMode.AUTO
): ClientStaticData {
    val useOwn = when (mode) {
        BusinessIdentityMode.NETWORK -> false
        BusinessIdentityMode.BRANCH -> hasValidOwnBusinessIdentity(date)
        BusinessIdentityMode.AUTO -> billsBranchIdentity() && hasValidOwnBusinessIdentity(date)
    }
    val source: Client = if (useOwn) this else (masterBranch ?: this)
    val ownName = source.getOwnBusinessName(date)
    return ClientStaticData(
        source.id,
        if (ownName.isNullOrEmpty()) source.getName(date) else ownName,
        documentBusinessId(date, source),
        source.getAddress(date) ?: "",
        source.getPhone(date) ?: ""
    )
}

/**
 * מס עוסק for a document dated [date], taken from [source] (the billed client, or
 * its network master). A non-empty value on that date is kept. When the document
 * is in a previous month and that dated value is blank, the current id on the
 * billed client, then on its master, then on this client, is used instead.
 */
fun Client.documentBusinessId(date: String, source: Client = this): String {
    val dated = source.getOwnBusinessId(date)?.trim().orEmpty()
    if (dated.isNotEmpty() || !DatesManipulator.isBeforeCurrentMonth(date)) return dated
    val today = DatesManipulator.dateNow()
    val candidates = listOf(
        source.getOwnBusinessId(today),
        source.masterBranch?.getOwnBusinessId(today),
        if (source.id != id) getOwnBusinessId(today) else null,
        if (source.id != id) masterBranch?.getOwnBusinessId(today) else null,
    )
    return candidates.firstNotNullOfOrNull { it?.trim()?.takeIf { id -> id.isNotEmpty() } } ?: ""
}